'use client'

import { Suspense, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { ChatboxPosition, Crisp } from 'crisp-sdk-web'
import { publicEnv } from '@/lib/env'
import { incrementCrispUnreadCount, resetCrispUnreadCount } from '@/hooks/use-crisp-unread-count'

/**
 * Mounts the Crisp chat widget SDK. Renders no DOM of its own — Crisp injects
 * its own elements into the page outside React's tree.
 *
 * `SupportCard` (the sidebar "Help" row) only exists inside the
 * authenticated dashboard shell (`/app/**`). There, Crisp's own floating
 * launcher bubble is redundant with it, so it's hidden — the Help row is the
 * only trigger, and the widget re-hides itself whenever the chat window
 * closes. Everywhere else (marketing, onboarding) there is no other trigger,
 * so the floating launcher stays visible as Crisp's default entry point.
 *
 * WHEN: rendered once, as a sibling of <Toaster> in the root layout, so it
 * mounts on every route by default.
 * NEVER: on `/report/**` (white-labelled client reports must not leak
 * RankSurf's support widget — PRD-14) or `/audit/**` (Crisp's launcher has no
 * vertical-offset config and would collide with the mobile sticky CTA bar).
 */
export function CrispChat(): React.JSX.Element {
  return (
    <Suspense fallback={null}>
      <CrispChatGate />
    </Suspense>
  )
}

/**
 * `usePathname()` opts its subtree into client rendering, so the Suspense boundary
 * above is required — without it the whole route tree would be forced dynamic
 * (mirrors `SearchParamsPageview`'s identical constraint).
 */
function CrispChatGate(): React.JSX.Element | null {
  const pathname = usePathname()

  if (pathname.startsWith('/report') || pathname.startsWith('/audit')) return null

  return <CrispSdkMount hasSidebarTrigger={pathname.startsWith('/app')} />
}

/** Isolated so the mount-once effect only ever runs on a route where Crisp is allowed. */
function CrispSdkMount({ hasSidebarTrigger }: { hasSidebarTrigger: boolean }): null {
  // Tracks whether the chat window is open, so a route change never hides it
  // mid-conversation. Read-model only — never read during render, only inside
  // effects and Crisp's own event callbacks, so it can't trip the "no refs
  // during render" rule.
  //
  // Deliberately NOT `Crisp.chat.isChatOpened()`: that reads `window.$crisp.is`,
  // which only exists once Crisp's own script has replaced the push-only stub
  // queue — calling it before the widget finishes loading throws
  // "window.$crisp.is is not a function". `hide()`/`show()`/`onChatOpened()`/
  // `onChatClosed()` are all queued `push()` calls and safe pre-load; `is()` is
  // a synchronous read and is not.
  const isChatOpenRef = useRef(false)

  useEffect(() => {
    if (!publicEnv.crispWebsiteId) return // soft-disable, matches the Turnstile pattern

    Crisp.configure(publicEnv.crispWebsiteId)
    // SDK-internal errors must not throw into the app (REL-24).
    Crisp.setSafeMode(true)
    // Radix overlays (Dialog/Sheet/Popover/DropdownMenu/Select/Tooltip) all sit at
    // z-50, as does the /audit mobile sticky CTA bar. 40 keeps the Crisp window
    // above ordinary page content but below every overlay in the codebase.
    Crisp.setZIndex(40)
    // Sonner's <Toaster> (src/app/layout.tsx) has no `position` prop, so it
    // defaults to bottom-right — the same corner Crisp's launcher occupies by
    // default. Move Crisp to the left rather than touch the app-wide toast
    // position, which other surfaces may depend on.
    Crisp.setPosition(ChatboxPosition.Left)
    Crisp.chat.onChatOpened(() => {
      isChatOpenRef.current = true
      resetCrispUnreadCount()
    })
    // Hiding the launcher on /app/** (below) hides Crisp's own unread badge with
    // it — the sidebar "Help" row (`SupportCard`) needs its own. crisp-sdk-web has no `onUnreadCountChanged`
    // event and its `unreadCount()` getter shares `isChatOpened()`'s pre-load
    // crash risk, so the count is tracked independently here instead: every
    // operator message received while the window is closed increments it.
    Crisp.message.onMessageReceived(() => {
      if (!isChatOpenRef.current) incrementCrispUnreadCount()
    })
  }, [])

  useEffect(() => {
    if (!publicEnv.crispWebsiteId) return

    // Don't hide a chat window mid-conversation — e.g. navigating from marketing
    // into /app (via login/redirect) while a chat is already open. Deferred to
    // onChatClosed below, which re-checks the route at the moment the user
    // actually closes it.
    if (hasSidebarTrigger) {
      if (!isChatOpenRef.current) Crisp.chat.hide()
    } else {
      Crisp.chat.show()
    }

    // `onChatClosed` de-registers any previous handler itself (crisp-sdk-web
    // calls `offChatClosed()` internally before adding the new one), so
    // re-registering here on every route change is safe — no stacked
    // handlers, and the closure always sees the current route.
    Crisp.chat.onChatClosed(() => {
      isChatOpenRef.current = false
      if (hasSidebarTrigger) Crisp.chat.hide()
    })
  }, [hasSidebarTrigger])

  return null
}
